<template lang="html">
  <div ref="overlay" v-if="show" :class="$style.overlay" :data-animate="animate">
    <div :class="$style.overlayInner">
      <article :class="$style.box" :data-type="type">
        <button :class="$style.closeButton" @click="handleClose">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="none"
            :class="[$style.closeIcon, 'close-icon']"
          >
            <path stroke-width="3" d="m2 2 20.003 20.002M22.002 2 2 22.002" />
          </svg>
        </button>
        <div :class="$style.boxInner">
          <picture :class="$style.picture">
            <source :srcset="images[1]" :media="`(min-width: ${breakpoints.mdMin})`" />
            <img :src="images[0]" :alt="button" />
          </picture>
          <div :class="$style.content">
            <h1 v-html="title" :class="$style.title" />
            <p v-html="caption" :class="$style.caption" />
            <button v-html="button" :class="[$style.button, 'button button--primary']" @click="handleModalOpen" />
          </div>
        </div>
      </article>
    </div>
  </div>
</template>

<script>
import { BREAKPOINTS } from '~scripts/core/breakpoints';
import { getScrollbarWidth } from '~scripts/core/helpers';

export default {
  name: 'VSlyshKupi',
  props: {
    timeToShowAfterPresence: {
      type: Number,
      default: 30000, // 30 seconds
    },
    timeToShowAfterClose: {
      type: Number,
      default: 86400000, // 24 hours
    },
    timeToShowAfterSubmit: {
      type: Number,
      default: 2592000000, // 30 days
    },
    type: {
      type: String,
      default: 'portrait',
    },
    images: {
      type: Array,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    caption: {
      type: String,
      required: true,
    },
    button: {
      type: String,
      required: true,
    },
    modalToOpen: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      currentTime: Date.now(),
      show: false,
      animate: '',
      observer: null,
      timeout: null,
    };
  },
  computed: {
    breakpoints() {
      return BREAKPOINTS;
    },
    modalElement() {
      return document.getElementById(this.modalToOpen);
    },
    modalForm() {
      return this.modalElement.querySelector('form');
    },
  },
  mounted() {
    this.enableTimeout();

    this.handleModalObserve();

    this.modalForm.addEventListener('submit', this.handleSubmit, { passive: true });
  },
  beforeDestroy() {
    this.disableTimeout();

    this.observer.disconnect();

    this.modalForm.removeEventListener('submit', this.handleSubmit);
  },
  methods: {
    getTimeToShow() {
      const timeToShow = localStorage.getItem('time-to-show');

      if (timeToShow) {
        const timeToShowLeft = parseInt(timeToShow, 10) - this.currentTime;

        if (timeToShowLeft > this.timeToShowAfterPresence) {
          return timeToShowLeft;
        }
      }

      localStorage.removeItem('time-to-show');

      return this.timeToShowAfterPresence;
    },
    handleShow() {
      const scrollbarWidth = getScrollbarWidth();

      document.documentElement.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);
      document.body.classList.add('no-scroll');

      this.show = true;
      this.animate = 'show';
    },
    handleHide(removeScroll = true) {
      this.animate = 'hide';

      this.$refs.overlay.addEventListener(
        'animationend',
        () => {
          this.$refs.overlay.scrollTop = 0;

          this.show = false;

          if (removeScroll) {
            document.body.classList.remove('no-scroll');
            document.documentElement.style.removeProperty('--scrollbar-width');
          }
        },
        {
          once: true,
        }
      );
    },
    handleClose(removeScroll) {
      this.handleHide(removeScroll);

      localStorage.setItem('time-to-show', JSON.stringify(this.currentTime + this.timeToShowAfterClose));

      this.disableTimeout();
      this.enableTimeout();
    },
    handleModalOpen() {
      this.handleClose(false);

      const modalAction = document.querySelector(`[data-modal="#${this.modalToOpen}"]`);
      modalAction.click();
    },
    handleModalObserve() {
      const disableTimeout = () => {
        this.disableTimeout();
      };
      const enableTimeout = () => {
        this.enableTimeout();
      };

      function callback(mutationList) {
        mutationList.forEach(function (mutation) {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            const modalElement = mutation.target;

            if (modalElement.classList.contains('modal--open')) {
              disableTimeout();
            } else {
              enableTimeout();
            }
          }
        });
      }

      this.observer = new MutationObserver(callback);
      this.observer.observe(this.modalElement, { attributes: true });
    },
    handleSubmit() {
      localStorage.setItem('time-to-show', JSON.stringify(this.currentTime + this.timeToShowAfterSubmit));

      //TODO: Отправка синхронная и срабатывает редирект, поэтому пока отключено
      // this.disableTimeout();
      // this.enableTimeout();
    },
    enableTimeout() {
      this.timeout = setTimeout(this.handleShow, this.getTimeToShow());
    },
    disableTimeout() {
      clearTimeout(this.timeout);
    },
  },
};
</script>

<style module lang="scss">
@use '~styles/core/_variables.scss' as vars;
@use '~styles/core/_mixins.scss' as mixins;

// Overlay
@keyframes showOverlay {
  0% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
}
@keyframes hideOverlay {
  0% {
    opacity: 1;
  }

  100% {
    opacity: 0;
  }
}

.overlay {
  position: fixed;
  z-index: 9999;
  inset: 0;
  display: flex;
  padding: 60px 20px;
  overflow-x: hidden;
  overflow-y: auto;
  background-color: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(5px);
  opacity: 0;
  animation-duration: vars.$transitionDurationSlow;
  animation-timing-function: ease-out;
  animation-fill-mode: forwards;
  will-change: opacity;

  &[data-animate='show'] {
    animation-name: showOverlay;
  }

  &[data-animate='hide'] {
    animation-name: hideOverlay;
  }

  @at-root [data-page='/enterprise'] {
    .overlay {
      background-color: rgba(0, 0, 0, 0.9);
    }
  }
}

.overlayInner {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: auto;
}

// Box
@keyframes showBox {
  0% {
    opacity: 0;
    transform: translateY(-50px);
  }

  100% {
    opacity: 1;
    transform: translateY(0);
  }
}
@keyframes hideBox {
  0% {
    opacity: 1;
    transform: translateY(0);
  }

  100% {
    opacity: 0;
    transform: translateY(-50px);
  }
}

.box {
  @include mixins.media(md) {
    position: relative;
    opacity: 0;
    transform: translateY(-50px);
    animation-duration: vars.$transitionDurationSlow;
    animation-timing-function: ease-out;
    animation-fill-mode: forwards;
    will-change: transform, opacity, scroll-position;

    @at-root [data-animate='show'] {
      .box {
        animation-name: showBox;
      }
    }

    @at-root [data-animate='hide'] {
      .box {
        animation-name: hideBox;
      }
    }
  }

  // Types
  &[data-type='landscape'] {
    .boxInner {
      overflow: hidden;

      @include mixins.media(md) {
        display: flex;
        align-items: center;
        width: 800px;
      }
    }

    .closeButton {
      transition: background-color vars.$transitionDurationFast;

      @include mixins.hover {
        &:hover {
          background-color: #fff;

          .closeIcon > path {
            stroke: vars.$colorSystem;
          }
        }
      }
    }

    .picture {
      @include mixins.media(md) {
        width: 320px;
        align-self: stretch;
        order: 2;
      }

      > img {
        width: 100%;
        object-fit: cover;
        object-position: center;
      }
    }

    .content {
      padding: 4rem 2.4rem;

      @include mixins.media(md) {
        flex: 1;
        order: 1;
        padding: 6rem;
      }
    }
  }

  &[data-type='portrait'] {
    @include mixins.media(md) {
      width: 600px;
    }

    .boxInner {
      padding: 4rem 3rem;
      text-align: center;

      @include mixins.media(md) {
        padding: 6rem;
      }
    }

    .picture {
      margin-bottom: 2rem;

      @include mixins.media(md) {
        margin-bottom: 2.4rem;
      }

      > img {
        max-width: 100%;
      }
    }
  }
}

@keyframes showBoxInner {
  0% {
    opacity: 0;
    transform: translateY(-20px);
  }

  100% {
    opacity: 1;
    transform: translateY(0);
  }
}
@keyframes hideBoxInner {
  0% {
    opacity: 1;
    transform: translateY(0);
  }

  100% {
    opacity: 0;
    transform: translateY(-20px);
  }
}

.boxInner {
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 18px 66px rgba(46, 51, 67, 0.12);

  @include mixins.media(sm, max) {
    max-width: 440px;
    opacity: 0;
    transform: translateY(-20px);
    animation-duration: vars.$transitionDurationSlow;
    animation-timing-function: ease-out;
    animation-fill-mode: forwards;
    will-change: transform, opacity, scroll-position;

    @at-root [data-animate='show'] {
      .boxInner {
        animation-name: showBoxInner;
      }
    }

    @at-root [data-animate='hide'] {
      .boxInner {
        animation-name: hideBoxInner;
      }
    }
  }

  @include mixins.media(md) {
    border-radius: 28px;
  }
}

// Close
.closeButton {
  position: absolute;
  z-index: 2;
  top: 0;
  right: 0;
  padding: 20px;
  cursor: pointer;

  @include mixins.hover {
    &:hover {
      .closeIcon > path {
        stroke: vars.$colorSystem;
      }
    }
  }

  @include mixins.media(md) {
    @include mixins.size(46px);

    top: 20px;
    right: 20px;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: unset;
    background-color: rgba(#fff, 0.5);
    backdrop-filter: blur(5px);
    border-radius: 50%;
  }
}

.closeIcon {
  vertical-align: top;

  > path {
    stroke: rgba(vars.$colorBase, 0.4);
  }

  @at-root [data-page='/enterprise'] {
    .closeIcon > path {
      @include mixins.media(sm, max) {
        stroke: rgba(#fff, 0.4);
      }
    }
  }
}

// Picture
.picture {
  display: block;

  > img {
    vertical-align: top;
  }
}

// Title
.title {
  @include mixins.font(2.6rem, 1.2);

  margin-bottom: 1rem;
  color: vars.$colorBase;
  font-weight: vars.$fontWeightBase;

  @include mixins.media(md) {
    @include mixins.font(3.5rem, 1.18);
  }
}

// Caption
.caption {
  @include mixins.font(1.5rem, 1.6);

  margin-bottom: 2rem;
  color: rgba(vars.$colorBase, 0.5);

  @include mixins.media(md) {
    @include mixins.font(1.9rem, 1.55);

    margin-bottom: 3.2rem;
  }
}

// Button
.button {
  @include mixins.font(1.6rem);
  @include mixins.interact(
    (
      color: #fff,
      background: vars.$colorPrimaryHover,
    )
  );

  padding: 1.6rem 2.8rem;
  background: vars.$colorPrimary;
  color: #fff;
  cursor: pointer;

  @include mixins.media(md) {
    @include mixins.font(2rem);

    padding: 1.6rem 3.6rem;
  }
}
</style>
